import classNames from 'classnames';

import React, { forwardRef, ForwardRefExoticComponent, RefAttributes } from 'react';

import { Error } from '../error';
import { Field } from '../field';
import type { FieldProps } from '../field/field';
import { Hint } from '../hint';
import { Icon } from '../icon';
import { Image } from '../image';

import styles from './phone-field.scss';

type Props = RefAttributes<HTMLDivElement> &
  Omit<FieldProps, 'type'> & {
    countryCode?: string;
    flagImage?: string;
    onFlagClick?: () => void;
    isVisibleFlagArrow?: boolean;
  };

const COMPONENT_ID = 'modulor-phone-field';

export const PhoneField: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      countryCode,
      flagImage,
      onFlagClick,
      isVisibleFlagArrow = true,
      label = '',
      name = '',
      value = '',
      error,
      disabled = false,
      readOnly,
      onChange,
      onClearClick,
      onActionIconClick,
      onFocus,
      onBlur,
      onKeyUp,
      onKeyDown,
      placeholderMask = '',
      isRequired,
      clearVisible,
      autoComplete,
      dataTestId = COMPONENT_ID,
      className,
      hint,
      ...rest
    },
    ref,
  ) => {
    const handleFlagClick = () => {
      if (onFlagClick && !disabled) {
        onFlagClick();
      }
    };

    return (
      <div
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        ref={ref}
        className={className}
        {...rest}>
        <div className={styles['field-group']}>
          <button
            id="flag-button"
            type="button"
            data-testid={`${dataTestId}-flag-button`}
            className={classNames(styles['flag-button'], {
              [styles.error]: !disabled && error,
              [styles.disabled]: disabled,
              [styles.dropdown]: isVisibleFlagArrow && !disabled,
            })}
            onClick={handleFlagClick}>
            <Image
              dataTestId={`${dataTestId}-flag-image`}
              alt="flag-image"
              width="24"
              height="24"
              name={countryCode?.toLowerCase()}
              src={flagImage}
            />
            {isVisibleFlagArrow && <Icon name="chevron_up_chevron_down"></Icon>}
          </button>
          <Field
            inputMode="numeric"
            label={label}
            name={name}
            autoComplete={autoComplete}
            error={!disabled && error}
            disabled={disabled}
            onActionIconClick={onActionIconClick}
            readOnly={readOnly}
            alwaysActive={true}
            value={value}
            placeholderMask={placeholderMask}
            onClearClick={onClearClick}
            type="text"
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            isRequired={isRequired}
            clearVisible={clearVisible}
            dataTestId={`${dataTestId}-field`}
          />
        </div>
        {!disabled && error ? (
          <Error
            error={typeof error === 'string' ? error : null}
            dataTestId={`${dataTestId}-error`}></Error>
        ) : null}
        {hint && !error ? <Hint hint={hint} dataTestId={`${dataTestId}-hint`} /> : null}
      </div>
    );
  },
);

PhoneField.displayName = 'PhoneField';
